import { useState } from 'react';
import { Badge, Button, ButtonGroup, Col, Row, Stack } from 'react-bootstrap';
import { I18nextProvider } from 'react-i18next';

import {
  faArrowRotateRight,
  faEye,
  faPenToSquare,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import i18nReport from 'src/translation/i18nReport';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import WarnNoConnectedCameraModal from 'src/components/custom_components/WarnNoConnectedCameraModal';
import OHResults from 'src/components/OHResults/OHResults';
import ReportEditor from 'src/components/ReportEditor';
import ReportViewer from 'src/components/ReportViewer';
import { ResumeInformationModal } from 'src/components/ResumeInformationModal';

import { OPTAIN_COLS_KEY } from 'src/models/AIIntegratedResultsTab.model';
import { Api } from 'src/models/Api.model';
import { Camera } from 'src/models/Camera.model';
import { CameraMode } from 'src/models/CameraMode.model';
import { getLocaleCode, Language } from 'src/models/Language.model';
import { ReportTemplate } from 'src/models/ReportTemplate.model';
import {
  CompletedReasonEnum,
  ExamStatusEnum,
  Session,
} from 'src/models/Session.model';

import useGetExamGetPendingStatusText from 'src/hooks/useGetExamPendingStatusText';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import { useHandleViewReport } from 'src/hooks/useHandleViewReport';
import useLoadSessions from 'src/hooks/useLoadSessions';
import useUiTranslation from 'src/hooks/useUiTranslation';

import getGradingResult from 'src/utils/getGradingResult';

const AIIntegratedResults = () => {
  const [showViewer, setShowViewer] = useState<boolean>(false);
  const [showEditor, setShowEditor] = useState<boolean>(false);

  const handleSetShowViewer = (showViewer: boolean) => {
    setShowViewer(showViewer);
  };

  const handleSetShowEditor = (showEditor: boolean) => {
    setShowEditor(showEditor);
  };

  const [currentSession, setCurrentSession] = useState<Session>();

  const handleSetCurrentSession = (currentSession: Session) => {
    setCurrentSession(currentSession);
  };

  const { tCommon, tPages, language } = useUiTranslation();

  const {
    userSettings: { userPermission },
    practiceSettings: { camera },
    cameraData,
    cameraMode,
  } = useRemoteBoundStore();

  const { handleHttpRequestError } = useHandleHttpRequestError();

  const { handleViewReport } = useHandleViewReport();

  const onViewReport = async (session: Session) => {
    const updatedSession = await handleViewReport(session);
    handleSetCurrentSession(updatedSession);
    handleSetShowViewer(true);
  };

  const handleEditReport = async (session: Session) => {
    try {
      const allowed = userPermission.update;

      if (!allowed) {
        throw new Error(tCommon('message.error.noPermissionEditReport'));
      }

      handleSetCurrentSession({
        ...session,
        template: session.order?.OrderType.value,
      });
      handleSetShowEditor(true);
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: `${tCommon('message.error.failedToEditReport')} ${errorMessage}`,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
    }
  };

  const [isShowResumeModal, setIsShowResumeModal] = useState(false);

  const handleSetIsShowResumeModal = (isShowResumeModal: boolean) => {
    setIsShowResumeModal(isShowResumeModal);
  };

  const [
    shouldShowWarnNoConnectedCameraModal,
    setShouldShowWarnNoConnectedCameraModal,
  ] = useState(false);

  const handleSetShouldShowWarnNoConnectedCameraModal = (
    shouldShowWarnNoConnectedCameraModal: boolean,
  ) => {
    setShouldShowWarnNoConnectedCameraModal(
      shouldShowWarnNoConnectedCameraModal,
    );
  };

  const {
    currentPage,
    currentSearchValue,
    handleSetCurrentPage,
    handleSetCurrentSearchValue,
    isLoading,
    dataResource,
    handleLoadSessions,
  } = useLoadSessions();
  const getPendingStatusText = useGetExamGetPendingStatusText();

  return (
    <Stack className="h-100">
      <Row>
        <h3>{tPages('results.title')}</h3>
        <div className="text-primary mb-4 d-flex gap-2 align-items-center">
          <FontAwesomeIcon icon={faEye} />
          {tPages('results.view')}
          <span>|</span>
          <FontAwesomeIcon icon={faPenToSquare} />
          {tPages('results.edit')}
          <span>|</span>
          <FontAwesomeIcon icon={faArrowRotateRight} />
          {tPages('results.resume')}
        </div>
      </Row>
      <Row className="flex-grow-1" style={{ minHeight: 0 }}>
        <Col className="d-flex flex-column h-100">
          <div className="flex-grow-1" style={{ minHeight: 0 }}>
            <OHResults
              currentPage={currentPage}
              currentSearchValue={currentSearchValue}
              handleSetCurrentPage={handleSetCurrentPage}
              handleSetCurrentSearchValue={handleSetCurrentSearchValue}
              isLoading={isLoading}
              dataResource={dataResource}
              handleLoadSessions={handleLoadSessions}
              extraColumns={[
                {
                  title: tPages('results.sessionStatus'),
                  dataIndex: OPTAIN_COLS_KEY.STATUS,
                  render: (record) => {
                    const { isLeftEyeGradable, isRightEyeGradable } =
                      getGradingResult(record);

                    return (
                      <div>
                        {!record.graded && (
                          <Badge bg="warning">
                            {getPendingStatusText(record)}
                          </Badge>
                        )}

                        {record.graded && !record.completedReason && (
                          <Badge bg="secondary">
                            {tCommon(
                              `examFields.examStatusEnum.${ExamStatusEnum.IN_PROGRESS}`,
                            )}
                          </Badge>
                        )}

                        {record.graded &&
                          record.completedReason ===
                            CompletedReasonEnum.AUTO_COMPLETE && (
                            <Badge bg="danger">
                              {tCommon(
                                `examFields.examStatusEnum.${ExamStatusEnum.INCOMPLETE}`,
                              )}
                            </Badge>
                          )}

                        {record.completedReason ===
                          CompletedReasonEnum.COMPLETED_EXAM &&
                          (record.leftResults?.length
                            ? isLeftEyeGradable
                            : true) &&
                          (record.rightResults?.length
                            ? isRightEyeGradable
                            : true) && (
                            <Badge bg="success">
                              {tCommon(
                                `examFields.examStatusEnum.${ExamStatusEnum.EXAM_SUCCESSFUL}`,
                              )}
                            </Badge>
                          )}

                        {(record.completedReason ===
                          CompletedReasonEnum.END_SESSION ||
                          (record.completedReason ===
                            CompletedReasonEnum.COMPLETED_EXAM &&
                            ((!!record.leftResults?.length &&
                              !isLeftEyeGradable) ||
                              (!!record.rightResults?.length &&
                                !isRightEyeGradable)))) && (
                          <Badge bg="info">
                            {tCommon(
                              `examFields.examStatusEnum.${ExamStatusEnum.EXAM_ENDED}`,
                            )}
                          </Badge>
                        )}
                      </div>
                    );
                  },
                },
                {
                  title: tPages('results.actions'),
                  dataIndex: OPTAIN_COLS_KEY.ACTIONS,
                  render: (record) => {
                    return (
                      <ButtonGroup
                        aria-label="Review report, resume and edit report"
                        className="gap-2"
                      >
                        <Button
                          variant="outline-primary"
                          onClick={() => onViewReport(record)}
                          hidden={
                            !record.order ||
                            !record.graded ||
                            (record.graded && !record.completedReason)
                          }
                          className="rounded-3 px-3 py-1"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </Button>
                        <Button
                          variant="outline-primary"
                          hidden={
                            !(record.graded && !record.completedReason) ||
                            !userPermission.create
                          }
                          onClick={() => {
                            if (
                              camera === Camera.SMALL &&
                              cameraMode === CameraMode.API &&
                              !cameraData
                            ) {
                              handleSetShouldShowWarnNoConnectedCameraModal(
                                true,
                              );
                              return;
                            }
                            handleSetCurrentSession({
                              ...record,
                              template: record.order?.OrderType.value,
                            });
                            handleSetIsShowResumeModal(true);
                          }}
                          className="rounded-3 px-3 py-1"
                          data-test="resume-exam-button"
                        >
                          <FontAwesomeIcon icon={faArrowRotateRight} />
                        </Button>
                        {record.order?.OrderType.value !==
                          ReportTemplate.OPTAIN_SMALL_DR && (
                          <Button
                            variant="outline-primary"
                            data-test="edit-report-button"
                            onClick={() => handleEditReport(record)}
                            hidden={
                              !record.order ||
                              !record.graded ||
                              (record.graded && !record.completedReason)
                            }
                            className="rounded-3 px-3 py-1"
                          >
                            <FontAwesomeIcon icon={faPenToSquare} />
                          </Button>
                        )}
                      </ButtonGroup>
                    );
                  },
                },
              ]}
            />
          </div>
        </Col>
      </Row>
      {currentSession && (
        <>
          {showViewer && (
            <I18nextProvider i18n={i18nReport}>
              <ReportViewer
                show={showViewer}
                handleCloseReportViewer={() => {
                  handleSetShowViewer(false);
                }}
                session={currentSession}
              />
            </I18nextProvider>
          )}

          {showEditor && (
            <ReportEditor
              currentPage={currentPage}
              currentSearchValue={currentSearchValue}
              handleLoadSessions={handleLoadSessions}
              show={showEditor}
              setShow={handleSetShowEditor}
              session={currentSession}
            />
          )}

          <ResumeInformationModal
            currentSession={currentSession}
            isShowResumeModal={isShowResumeModal}
            handleSetIsShowResumeModal={handleSetIsShowResumeModal}
          />
        </>
      )}
      <WarnNoConnectedCameraModal
        isVisible={shouldShowWarnNoConnectedCameraModal}
      />
    </Stack>
  );
};

export default AIIntegratedResults;
